import axios from "axios";
import * as pdfjs from "pdfjs-dist";
import { filesService } from "@/services/filesService";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).toString();

/**
 * Validates that a URL is a valid S3 file URL based on the backend validation logic.
 * This mirrors the validation in backend/portal/utils/file_storage.py::_regenerate_s3_link
 *
 * Valid URLs:
 * - http://localhost or http://127.0.0.1 (for local development)
 * - https://{bucket_name}.s3.amazonaws.com (any S3 bucket)
 *
 * This prevents XSS attacks via dangerous URL schemes like javascript:, data:, etc.
 *
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is a valid S3 file URL, false otherwise
 */
export const isValidS3FileUrl = (url) => {
  if (!url || typeof url !== "string") return false;

  // Allow localhost for local development (matches backend logic at line 88)
  const isLocalhost = url.startsWith("http://localhost") || url.startsWith("http://127.0.0.1");
  if (isLocalhost) {
    // Validate localhost format matches expected pattern: http://localhost:<port>/<bucket>/<object_key>?<query_string>
    // This matches the regex pattern used in backend at line 100
    const localhostPattern = /^http:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/.+?\/.+?(\?|$)/;
    return localhostPattern.test(url);
  }

  // Validate S3.amazonaws.com URLs (matches backend logic at lines 91-92)
  // Pattern: https://{bucket_name}.s3.amazonaws.com/{object_key}?{query_string}
  // The backend checks: link.startswith(f"https://{cls.bucket_name()}.s3.amazonaws.com")
  // We validate any bucket name since we don't have access to the bucket name in frontend
  const s3Pattern = /^https:\/\/[^/]+\.s3\.amazonaws\.com\/.+/;
  return s3Pattern.test(url);
};

// LAM-3580 / LAM-3649: Image files (TIFF, JPEG, PNG) are routed through the
// backend convert-and-upload endpoint which converts them to PDF before storing.
// PDFs continue on the fast presigned direct-to-S3 path.
const IMAGE_MIME_TYPES = new Set(["image/tiff", "image/jpeg", "image/png"]);
const IMAGE_EXT_REGEX = /\.(tiff?|jpe?g|png)$/i;
const ALLOWED_FILE_TYPES = ["application/pdf", "image/tiff", "image/jpeg", "image/png"];
const DEFAULT_MAX_SIZE_BYTES = 20 * 1024 * 1024;

export const needsServerConversion = (file, fileName) =>
  IMAGE_MIME_TYPES.has(file?.type) || IMAGE_EXT_REGEX.test(fileName || "");

/**
 * Validate file type and size before upload.
 * @param {File} file
 * @param {Object} [options]
 * @param {number|null} [options.maxSizeBytes] - Max file size in bytes (default 20MB; null skips the size check)
 * @returns {string|null} Error message, or null if valid
 */
export const validateFile = (file, { maxSizeBytes = DEFAULT_MAX_SIZE_BYTES } = {}) => {
  // LAM-3649: needsServerConversion() extension fallback catches image files on
  // browsers that report an empty file.type (macOS Safari etc.). Images are
  // transparently routed to /api/files/convert-and-upload/ inside uploadService.
  if (!ALLOWED_FILE_TYPES.includes(file.type) && !needsServerConversion(file, file.name)) {
    return "Invalid file type. Please upload a PDF, TIFF, JPG, or PNG file.";
  }
  if (maxSizeBytes != null && file.size > maxSizeBytes) {
    const maxSizeMb = Math.round(maxSizeBytes / (1024 * 1024));
    return `File is too large. Maximum file size is ${maxSizeMb}MB.`;
  }
  return null;
};

/**
 * Extract a user-facing error message from an upload failure.
 * @param {*} error
 * @returns {string}
 */
export const parseUploadError = (error) => {
  return error?.error?.response?.data?.error || error?.message || "File upload failed. Please try again.";
};

const validatePdf = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  let pdf;
  try {
    pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    if (pdf.numPages <= 0) {
      throw new Error("File is not a valid PDF: the document contains 0 pages.");
    }
  } catch (error) {
    if (error.message && error.message.includes("not a valid PDF")) {
      throw error;
    }
    throw new Error("File is not a valid PDF: the document could not be parsed.");
  } finally {
    if (pdf) {
      pdf.destroy();
    }
  }
};

/**
 * POST a file to a presigned S3 POST target. The api.js response interceptor camelizes
 * the presigned `fields`, so we un-camelize the two SigV2 keys S3 expects verbatim.
 * @param {{url: string, fields: Object}} upload - presigned POST data (camelized fields)
 * @param {File|Blob} file
 * @param {string} fileName
 */
export const uploadToPresignedPost = async (upload, file, fileName) => {
  const formData = new FormData();
  const replaceKeys = {
    aWSAccessKeyId: "AWSAccessKeyId",
    xAmzSecurityToken: "x-amz-security-token",
  };
  Object.entries(upload.fields).forEach(([key, value]) => {
    formData.append(replaceKeys[key] ?? key, value);
  });
  formData.append("file", new File([file], fileName));
  await axios.post(upload.url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const uploadService = {
  async uploadFile(file, fileName) {
    const { downloadUrl } = await this.uploadFileWithKey(file, fileName);
    return downloadUrl;
  },

  /**
   * Upload a file and return both the presigned download URL and the durable S3 key.
   * @returns {Promise<{downloadUrl: string, uploadKey: string}>}
   */
  async uploadFileWithKey(file, fileName) {
    if (needsServerConversion(file, fileName)) {
      const { downloadUrl, uploadKey } = await filesService.convertAndUpload(file);
      return { downloadUrl, uploadKey };
    }

    if (file.type === "application/pdf" || fileName?.toLowerCase().endsWith(".pdf")) {
      await validatePdf(file);
    }

    return this.uploadDirect(file, fileName);
  },

  // LAM-4851: pasted screenshots upload as-is (no PDF conversion) so case logs can render them inline.
  async uploadDirect(file, fileName) {
    const { upload, download } = await filesService.getPresignedUrl(fileName);
    await uploadToPresignedPost(upload, file, fileName);
    const verification = await filesService.verifyUpload(download, file.size);
    if (!verification.success) {
      throw new Error(verification.error || "File upload verification failed");
    }
    return { downloadUrl: download, uploadKey: `files/${fileName}` };
  },
};
